// @flow
import React, { Component } from "react";
import classNames from "classnames";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import { withStyles } from "@material-ui/core/styles";

import { selectStyles } from "./StyledSelect";
import type { StyledSelectClassesType } from "./StyledSelect";
import type { DictionaryType } from "../../types/GeneralTypes";

export const chipInputStyles = (theme: DictionaryType): DictionaryType => ({
  ...selectStyles(theme),
  root: {
    display: "flex",
    flexWrap: "wrap",
    position: "relative"
  },
  chip: {
    margin: theme.spacing.unit / 4,
    fontSize: "1rem !IMPORTANT"
  },
  // We had to use a lot of global selectors in order to style react-select.
  // We are waiting on https://github.com/JedWatson/react-select/issues/1679
  // to provide a better implementation.
  // Also, we had to reset the default style injected by the library.
  "@global": {
    ".Select-control": {
      display: "flex",
      alignItems: "center",
      border: 0,
      height: "auto",
      background: "transparent",
      "&:hover": {
        boxShadow: "none"
      }
    },
    ".Select-multi-value-wrapper": {
      flexGrow: 1,
      display: "flex",
      flexWrap: "wrap"
    },
    ".Select--multi .Select-input": {
      margin: 0
    },
    ".Select.has-value.is-clearable.Select--single > .Select-control .Select-value": {
      padding: "0 0 0 12px"
    },
    ".Select-noresults": {
      padding: theme.spacing.unit * 2
    },
    ".Select-input": {
      display: "inline-flex !important",
      padding: 0,
      height: "auto"
    },
    ".Select-input input": {
      background: "transparent",
      border: 0,
      padding: 0,
      cursor: "default",
      display: "inline-block",
      fontFamily: "inherit",
      fontSize: "inherit",
      margin: 0,
      outline: 0
    },
    ".Select-placeholder, .Select--single .Select-value": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.pxToRem(16),
      padding: 0
    },
    ".Select-placeholder": {
      opacity: 0.42,
      color: theme.palette.common.black,
      paddingLeft: theme.spacing.unit * 2
    },
    ".Select-menu-outer": {
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[2],
      position: "absolute",
      left: 0,
      top: `calc(100% + ${theme.spacing.unit}px)`,
      width: "100%",
      zIndex: 2,
      maxHeight: 48 * 4.5
    },
    ".Select.is-focused:not(.is-open) > .Select-control": {
      boxShadow: "none"
    },
    ".Select-menu": {
      maxHeight: 48 * 4.5,
      overflowY: "auto"
    },
    ".Select-menu div": {
      boxSizing: "content-box"
    },
    ".Select-arrow-zone, .Select-clear-zone": {
      color: theme.palette.action.active,
      cursor: "pointer",
      height: 21,
      width: 21,
      zIndex: 1
    },
    // Only for screen readers. We can"t use display none.
    ".Select-aria-only": {
      position: "absolute",
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      height: 1,
      width: 1,
      margin: -1
    }
  }
});


type ChipInputStatesType = {
  multi: Array<string> | string | null
};

type ChipInputClassesType = {
  ...StyledSelectClassesType,
  root: DictionaryType,
  chip: DictionaryType
};

type SelectValueType = {
  value: string,
  label: string
};

type ChipInputPropsType = {
  classes: ChipInputClassesType,
  tags: Array<string>,
  name: string,
  label: string,
  value: Array<string> | string,
  selectComponent: React$Element<*>,
  isRequired?: boolean,
  isInvalid?: boolean,
  syncChange: Function,
  placeholder?: string,
  allowCreate?: boolean,
  isStyled?: boolean,
  isMultiple?: boolean,
  isDisabled?: boolean
};

class ChipInputBase extends Component<ChipInputPropsType, ChipInputStatesType> {
  constructor(props: ChipInputPropsType) {
    super(props);
    this.state = {
      multi: props.value || null
    };
  }

  handleChangeMulti = (
    multi: Array<SelectValueType> | SelectValueType | null
  ) => {
    let value;
    if (Array.isArray(multi)) {
      value = [];
      for (let i = 0; i < multi.length; i++) {
        value.push(multi[i].value);
      }
    } else {
      value = multi ? multi.value : null;
    }

    this.setState({ multi: value }, () => {
      this.props.syncChange(this.props.name, value);
    });
  };

  componentDidUpdate(prevProps: ChipInputPropsType) {
    if (prevProps.value !== this.props.value) {
      this.setState({ multi: this.props.value });
    }
  }

  render(): * {
    const { classes, isStyled } = this.props;

    return (
      <FormControl
        fullWidth={true}
        className={classes.root}
        disabled={this.props.isDisabled}
      >
        {this.props.label !== "" && (
          <InputLabel
            htmlFor={this.props.name}
            required={this.props.isRequired}
            error={this.props.isInvalid}
            shrink={true}
            classes={{
              root: classes.greenLabel,
              formControl: isStyled
                ? classes.textFieldFormLabel
                : classes.labelFormControl,
              focused: classes.labelFocused,
              error: classes.labelError,
              disabled: classes.labelDisabled
            }}
          >
            {this.props.label}
          </InputLabel>
        )}
        <Input
          id={this.props.name}
          name={this.props.name}
          fullWidth={true}
          error={this.props.isInvalid}
          inputComponent={this.props.selectComponent}
          classes={{
            root: isStyled ? classes.textFieldRoot : classes.inputRootGreen,
            input: classNames({
              [classes.textFieldInput]: isStyled,
              [classes.inputFont]: !isStyled,
              [classes.borderError]: this.props.isInvalid
            }),
            underline: classes.underlineGreen,
            error: classes.inputError,
            disabled: classes.inputDisabled
          }}
          disableUnderline={isStyled ? true : false}
          inputProps={{
            classes,
            value: this.state.multi,
            multi: this.props.isMultiple,
            onChange: this.handleChangeMulti,
            placeholder: this.props.placeholder,
            instanceId: "react-select-chip",
            id: "react-select-chip",
            name: "react-select-chip",
            simpleValue: false,
            options: this.props.tags,
            required: this.props.isRequired
          }}
          disabled={this.props.isDisabled}
        />
      </FormControl>
    );
  }
}

export default withStyles(chipInputStyles)(ChipInputBase);
